import * as React from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const chipVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium leading-5",
  {
    variants: {
      variant: {
        default: "bg-surface text-ink-700 border border-line",
        active: "bg-p-600 text-white border border-p-700 font-medium",
        "dashed-plus": "border border-dashed border-line-strong text-ink-500 bg-transparent",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface ChipProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof chipVariants> {
  removable?: boolean;
  onRemove?: () => void;
}

export function Chip({ className, variant, removable, onRemove, children, ...props }: ChipProps) {
  const { t } = useTranslation();
  return (
    <button type="button" className={cn(chipVariants({ variant }), className)} {...props}>
      {children}
      {removable ? (
        <span
          role="button"
          aria-label={t("common.chip.remove.label")}
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onRemove?.();
            }
          }}
          className="-mr-1 inline-flex size-4 cursor-pointer items-center justify-center text-ink-400 hover:text-ink-700"
        >
          <X size={12} />
        </span>
      ) : null}
    </button>
  );
}
